import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider
} from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 12,
    width: 560,
    maxWidth: 600,
    minWidth: 360,
    background: '#FFFFFF',
    boxShadow: '0px 6px 24px rgba(0,0,0,0.15)',
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 28,
    paddingRight: 28,
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 6,
    paddingBottom: 8
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  warningIcon: {
    color: '#FFA726',
    fontSize: 28,
    flexShrink: 0
  },
  titleText: {
    fontWeight: 600,
    fontSize: 18,
    color: '#333333'
  },
  closeButton: {
    padding: 6,
    marginLeft: 8,
    color: '#757575',
    '&:hover': {
      color: '#000000'
    }
  },
  subheader: {
    fontWeight: 500,
    fontSize: 16,
    color: '#333333',
    marginBottom: 6,
    lineHeight: 1.4
  },
  bodyText: {
    color: '#666666',
    fontSize: 14,
    marginTop: 0,
    lineHeight: 1.4,
    maxWidth: 480
  },
  actions: {
    paddingTop: 8,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12
  },
  cancelButton: {
    textTransform: 'none',
    borderRadius: 24,
    padding: '8px 20px',
    border: '1px solid #C4C4C4',
    color: '#333333',
    backgroundColor: '#FFFFFF',
    fontWeight: 500,
    fontSize: 14,
    minWidth: 100,
    height: 36,
    '&:hover': {
      backgroundColor: '#F5F5F5'
    }
  },
  deleteButton: {
    backgroundColor: '#00445D',
    color: '#FFFFFF',
    borderRadius: 24,
    textTransform: 'none',
    padding: '8px 24px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    fontWeight: 500,
    fontSize: 14,
    minWidth: 100,
    height: 36,
    '&:hover': { backgroundColor: '#00374C' }
  }
}));

export default function DeleteFieldDialog({ open, onClose, onConfirm, fieldId }) {
  const classes = useStyles();

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') onConfirm(fieldId);
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      aria-labelledby="delete-field-dialog"
      PaperProps={{ className: classes.paper }}
      BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.45)' } }}
      transitionDuration={200}
      maxWidth="xs"
      fullWidth={false}
    >
      <Box className={classes.header}>
        <Box className={classes.titleRow}>
          <WarningOutlinedIcon className={classes.warningIcon} />
          <Typography className={classes.titleText}>Delete Field</Typography>
        </Box>
        <IconButton className={classes.closeButton} onClick={onClose} aria-label="close">
          <CloseIcon style={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Divider style={{ borderBottom: '1px solid #E0E0E0', marginTop: 8, marginBottom: 12 }} />

      <DialogContent>
        <Typography className={classes.subheader}>Confirm Deletion</Typography>
        <Typography className={classes.bodyText}>
          The field will be permanently deleted. This action cannot be undone.
        </Typography>
      </DialogContent>

      <Divider style={{ borderBottom: '1px solid #E0E0E0', marginTop: 12, marginBottom: 12 }} />

      <DialogActions className={classes.actions} style={{ paddingTop: 8, paddingBottom: 8 }}>
        <Button onClick={onClose} variant="outlined" className={classes.cancelButton}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" className={classes.deleteButton}>Yes, Delete</Button>
      </DialogActions>
    </Dialog>
  );
}
